import { LiveAgent } from '@gid/vue-common/components/ticketing/interface/ticketing';

/**
 * Shared, side-effect-free helpers for building a LiveAgent "create ticket"
 * payload. Extracted so the job-coupled creator (LiveAgentTicketNew.vue) and
 * the job-independent console creator (TicketCreateModal.vue) share one core
 * instead of forking the 500-line component.
 */

export const MAX_PAYLOAD_SIZE_IN_MB = 50;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when every comma-separated address in the string is a valid email. */
export function validateEmails(emailsString: string): boolean {
  return emailsString.split(',').every((email) => EMAIL_RE.test(email.trim()));
}

/**
 * Guard against LiveAgent's payload limit — base64 attachments inflate the JSON
 * quickly, so we size the serialized payload before POSTing.
 */
export function validatePayloadSize(
  payload: unknown,
  maxSizeInMB: number = MAX_PAYLOAD_SIZE_IN_MB,
): { isValid: boolean; sizeInMB: number } {
  const bytes = new TextEncoder().encode(JSON.stringify(payload)).length;
  const sizeInMB = bytes / (1024 * 1024);
  return { isValid: sizeInMB <= maxSizeInMB, sizeInMB };
}

/** A blank LiveAgent create-ticket payload (status `C` = open). */
export function emptyLiveAgentTicket(): LiveAgent.TicketInterface {
  return {
    subject: '',
    departmentid: '',
    recipient: '',
    message: '',
    carbon_copies: '',
    blind_carbon_copies: '',
    status: 'C',
    mail_message_id: null,
    do_not_send_mail: 'N',
    use_template: 'Y',
    is_html_message: 'Y',
    custom_fields: [],
    tags: [],
    attachments: [],
    filePaths: [],
  };
}
